/* ==========================================================================
   Shrimp Zone — wishlist
   --------------------------------------------------------------------------
   Drives both the heart button (snippets/sz-wishlist.liquid) and the wishlist
   page (sections/sz-wishlist-page.liquid).

   This lives in an external asset rather than inline <script> tags on purpose.
   The theme runs `navigation_type: spa`, so pages are swapped in client-side —
   and inline scripts inserted that way DO NOT EXECUTE. That is the same class
   of failure that stopped SnapBundle rendering earlier. Loading once from
   theme.liquid and re-rendering on DOM changes survives client-side navigation.

   State is localStorage, so per-BROWSER, not per-customer. No cross-device
   sync; clearing site data clears the list.
   ========================================================================== */

(() => {
  const KEY = 'sz:wishlist:v1';

  const read = () => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(v) ? v : [];
    } catch {
      // Corrupt or unavailable storage (private mode, quota, tampering).
      // Degrade to an empty list rather than throwing on every page load.
      return [];
    }
  };

  const write = (items) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch {
      /* storage full or blocked — the UI still reflects the click */
    }
    document.dispatchEvent(new CustomEvent('sz:wishlist:change', { detail: { items } }));
  };

  /* --- Heart buttons ---------------------------------------------------- */

  const paint = (btn, saved) => {
    btn.classList.toggle('is-saved', saved);
    btn.setAttribute('aria-pressed', String(saved));
    btn.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Save to wishlist');
  };

  const syncButtons = () => {
    const items = read();
    document.querySelectorAll('[data-sz-wishlist]').forEach((btn) => {
      paint(btn, items.some((i) => i.id === btn.dataset.productId));
    });
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-sz-wishlist]');
    if (!btn) return;
    e.preventDefault();

    const id = btn.dataset.productId;
    const items = read();
    const at = items.findIndex((i) => i.id === id);

    if (at > -1) {
      items.splice(at, 1);
    } else {
      // Store enough to render the wishlist page without re-fetching each
      // product; price is in cents, matching Shopify's own convention.
      items.push({
        id,
        handle: btn.dataset.productHandle,
        title: btn.dataset.productTitle,
        url: btn.dataset.productUrl,
        image: btn.dataset.productImage,
        price: btn.dataset.productPrice,
      });
    }

    write(items);
    paint(btn, at === -1);

    // Transient marker for the CSS ring burst (sz-wl-ring keyframes) — only on
    // a user SAVE, never on the page-load sync, which would fire rings for
    // every already-saved heart on every page view.
    if (at === -1) {
      btn.classList.add('sz-wl-pop');
      setTimeout(() => btn.classList.remove('sz-wl-pop'), 700);
    }
  });

  /* --- Wishlist page ---------------------------------------------------- */

  // Built with createElement rather than innerHTML on purpose. The values come
  // from localStorage, which the visitor can edit freely — self-XSS only, but
  // there is no reason to parse untrusted strings as HTML.
  const card = (item, money) => {
    const li = document.createElement('li');
    li.className = 'sz-wl__item';

    const link = document.createElement('a');
    link.className = 'sz-wl__link';
    link.href = item.url || '#';

    if (item.image) {
      const img = document.createElement('img');
      img.className = 'sz-wl__img';
      img.src = item.image;
      img.alt = item.title || '';
      img.loading = 'lazy';
      link.appendChild(img);
    }

    const title = document.createElement('span');
    title.className = 'sz-wl__title';
    title.textContent = item.title || 'Product';
    link.appendChild(title);

    const cents = Number(item.price);
    if (Number.isFinite(cents) && cents > 0) {
      const price = document.createElement('span');
      price.className = 'sz-wl__price';
      price.textContent = money ? money.format(cents / 100) : (cents / 100).toFixed(2);
      link.appendChild(price);
    }

    li.appendChild(link);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'sz-wl__remove';
    remove.dataset.removeId = item.id;
    remove.setAttribute('aria-label', 'Remove ' + (item.title || 'item') + ' from wishlist');
    remove.textContent = 'Remove';
    li.appendChild(remove);

    return li;
  };

  const renderPage = () => {
    const root = document.querySelector('[data-sz-wishlist-page]');
    if (!root) return;

    const grid = root.querySelector('[data-sz-wl-grid]');
    const empty = root.querySelector('[data-sz-wl-empty]');
    if (!grid || !empty) return;

    let money = null;
    try {
      money = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: root.dataset.currency || 'AUD',
      });
    } catch {
      /* unknown currency code — fall back to a plain number in card() */
    }

    const items = read();

    // Rebuild only when the set actually changed. replaceChildren always
    // mutates childList, and this runs inside a MutationObserver-driven
    // refresh — the observer is paused during writes, but skipping redundant
    // rebuilds also avoids destroying and recreating every card on any
    // unrelated DOM change on the page.
    const signature = items.map((i) => i.id).join(',');
    if (grid.dataset.szSig !== signature) {
      grid.dataset.szSig = signature;
      grid.replaceChildren(...items.map((i) => card(i, money)));
    }

    grid.hidden = items.length === 0;
    empty.hidden = items.length !== 0;

    if (!root.dataset.szWlBound) {
      root.dataset.szWlBound = '1';
      grid.addEventListener('click', (e) => {
        const btn = e.target.closest && e.target.closest('[data-remove-id]');
        if (!btn) return;
        write(read().filter((i) => i.id !== btn.dataset.removeId));
        renderPage();
      });
    }
  };

  /* --- Header count badge ----------------------------------------------- */

  const syncCount = () => {
    const n = read().length;
    const text = n > 99 ? '99+' : String(n);
    document.querySelectorAll('[data-sz-wishlist-count]').forEach((el) => {
      // Compare before writing. Assigning textContent replaces the child text
      // node even when the string is identical, which is a childList mutation.
      if (el.textContent !== text) el.textContent = text;
      // Hidden at zero so an untouched wishlist shows a clean icon. The badge
      // has an author `display` in CSS, so a [hidden] guard is required there —
      // the attribute alone would be overridden.
      el.hidden = n === 0;
    });
  };

  /* --- Wiring ----------------------------------------------------------- */

  // There is deliberately NO MutationObserver here.
  //
  // An earlier version watched document.body for childList changes so state
  // would survive client-side navigation. Because every sync function WRITES to
  // the DOM (textContent on the badge, replaceChildren on the page grid), that
  // was an infinite loop — write -> mutation -> observer -> write — and it took
  // every page down, since the header badge renders sitewide.
  //
  // It is not needed any more: navigation_type is now `mpa`, so every
  // navigation is a real page load and this script runs fresh each time. That
  // change was made to fix SnapBundle, which also only initialises on load.
  //
  // If navigation_type is ever set back to `spa`, this file needs a way to
  // re-sync after a client-side page swap — but any such mechanism must not
  // write to the DOM inside its own change handler.
  const refresh = () => {
    syncButtons();
    syncCount();
    renderPage();
  };

  // Other tabs.
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) refresh();
  });

  document.addEventListener('sz:wishlist:change', refresh);

  // Back/forward can restore a page from the bfcache without re-running the
  // script, so re-sync on show as well as at startup.
  window.addEventListener('pageshow', refresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
